<template>
  <div class="cart-bg">
    <div class="cart-main-box">
      <div class="cart-bar">
        <h2>{{ translations.cart.title }}</h2>
        <router-link to="/" class="cart-back-btn">&larr; {{ translations.cart.back_to_store }}</router-link>
      </div>

      <div v-if="cartItems.length === 0" class="cart-empty">{{ translations.cart.empty }}</div>

      <div v-else>
        <div
          v-for="item in cartItems"
          :key="`${item.productId}-${item.variantId || 'no-variant'}`"
          class="cart-table-row"
        >
          <div class="col col-product">
            <div class="prod-info">
              <img :src="item.image" alt="" />
              <div class="prod-col-text">
                <div class="prod-title">{{ item.name }}</div>
                <div class="prod-details" v-if="item.variant">
                  {{ translations.cart.variant }}: {{ item.variant.type && item.variant.type.name }}
                </div>
              </div>
            </div>
          </div>

          <div class="col col-price">
            <div class="prod-price">
              <b>{{ item.price.toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</b>
            </div>
          </div>

          <div class="col col-qty">
            <div class="qty-field">
              <button @click="decreaseQty(item.productId, item.variantId)">-</button>
              <span>{{ item.quantity }}</span>
              <button @click="increaseQty(item.productId, item.variantId)">+</button>
            </div>
          </div>

          <div class="col col-total">
            <b>{{ (item.price * item.quantity).toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</b>
          </div>

          <div class="col col-empty">
            <button class="remove-btn" @click="removeItem(item.productId, item.variantId)">×</button>
          </div>
        </div>

        <div v-if="summaryVisible" class="cart-summary">
          <div class="sum-row">
            <label for="country">{{ translations.cart.select_country }}</label>
            <select v-model="selectedCountry" id="country" class="country-select">
              <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                {{ c.name }}
              </option>
            </select>
          </div>

          <div class="sum-row">
            <span>{{ translations.cart.delivery }}:</span>
            <span><b>{{ shippingTotal.toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</b></span>
          </div>

          <div class="sum-row">
            <span>{{ translations.cart.total }}:</span>
            <span><b>{{ total.toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</b></span>
          </div>

          <div class="sum-row total-final">
            <span>{{ translations.cart.total_final }}:</span>
            <span><b>{{ (total + shippingTotal).toFixed(2) }} {{ getCurrencySymbol(currencyCode) }}</b></span>
          </div>

          <router-link to="/podsumowanie" custom v-slot="{ navigate }">
            <button @click="navigate" class="checkout-btn">{{ translations.cart.go_to_checkout }}</button>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { translations, currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'
import eventBus from '@/eventBus.js'

export default {
  name: 'AppCart',
  data() {
    return {
      cartItems: [],
      selectedCountry: 'PL',
      availableCountries: [],
      summaryVisible: false, // NOWE
    }
  },
  computed: {
    translations() { return translations.value },
    // alias waluty (żeby nie myliło się z importem)
    currencyCode() { return currentCurrency.value || 'PLN' },

    total() {
      return this.cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
    },

    // koszt wysyłki wg wybranej waluty
    shippingTotal() {
      const key = this.priceKeyForCurrency(this.currencyCode)
      const sel = (this.selectedCountry || '').toUpperCase()

      return this.cartItems.reduce((sum, item) => {
        const opts = item.variant && item.variant.shipping_options ? item.variant.shipping_options : []
        const opt = opts.find(o => String(o.country).toUpperCase() === sel)
        if (!opt) return sum
        const raw = Object.prototype.hasOwnProperty.call(opt, key) ? opt[key] : 0
        const v = Number(raw)
        return sum + (isFinite(v) ? v : 0) * (item.quantity || 1)
      }, 0)
    }
  },
  watch: {
    currencyCode() { this.loadCart() }
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    window.addEventListener('languageChanged', this.loadCart)
    window.addEventListener('currencyChanged', this.loadCart)

    // IP → LS (jeśli LS pusty)
    await this.prefetchIPCountry()
    await this.loadCart()
    this.syncHeaderBadge()
  },
  beforeUnmount() {
    window.removeEventListener('languageChanged', this.loadCart)
    window.removeEventListener('currencyChanged', this.loadCart)
  },
  methods: {
    /* === Waluty === */
    getCurrencySymbol(code) {
      const symbols = {
        PLN: 'zł', USD: '$', EUR: '€',
        CZK: 'Kč', HUF: 'Ft', BGN: 'лв',
        RON: 'lei', DKK: 'kr.', SEK: 'kr'
      }
      return symbols[code] || code
    },
    priceKeyForCurrency(code) {
      const map = {
        PLN:'price_pln', EUR:'price_eur', USD:'price_usd',
        CZK:'price_czk', HUF:'price_huf', BGN:'price_bgn',
        RON:'price_ron', DKK:'price_dkk', SEK:'price_sek'
      }
      return map[code] || 'price_pln'
    },

    /* === Autodetekcja kraju: LS → IP → navigator === */
    async prefetchIPCountry() {
      try {
        if (localStorage.getItem('ship_to_iso')) return
        const ctrl = new AbortController()
        const t = setTimeout(() => ctrl.abort(), 1500)
        const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal })
        clearTimeout(t)
        if (!res.ok) return
        const data = await res.json()
        const iso = data && data.country_code ? String(data.country_code).toUpperCase() : ''
        if (iso) localStorage.setItem('ship_to_iso', iso)
      } catch (e) {
        // no-op żeby nie było ESLint no-empty
        void 0
      }
    },
    getUserCountryISO() {
      const stored = localStorage.getItem('ship_to_iso')
      if (stored) return stored.toUpperCase()
      const m = (navigator.language || '').match(/-([A-Za-z]{2})$/)
      return m ? m[1].toUpperCase() : 'PL'
    },

    /* === Endonimy (nazwy krajów w ojczystym języku) === */
    regionToLang(code) {
      const m = {
        PL:'pl', DE:'de', AT:'de', BE:'nl', BG:'bg', HR:'hr', CZ:'cs', DK:'da', EE:'et',
        ES:'es', FR:'fr', NL:'nl', LT:'lt', LV:'lv', RO:'ro', SK:'sk', SI:'sl',
        SE:'sv', IT:'it', HU:'hu', PT:'pt', IE:'en'
      }
      const cc = String(code).toUpperCase()
      return m[cc] || 'en'
    },
    countryEndonym(iso) {
      const lang = this.regionToLang(iso)
      try {
        const dn = new Intl.DisplayNames([lang], { type: 'region' })
        return dn.of(String(iso).toUpperCase()) || String(iso).toUpperCase()
      } catch (e) {
        // no-op
        return String(iso).toUpperCase()
      }
    },
    countryLabel(iso, fallback) {
      const code = String(iso).toUpperCase()
      if (/^[A-Z]{2}$/.test(code)) return this.countryEndonym(code)
      return fallback || code
    },

    /* === Ładowanie koszyka === */
    async loadCart() {
      let rawCart = []
      try {
        rawCart = JSON.parse(localStorage.getItem('cart') || '[]')
      } catch (e) {
        rawCart = []
      }
      rawCart = rawCart.filter(e => e && e.slug && e.id)

      const priceKey = this.priceKeyForCurrency(this.currencyCode)

      const updated = await Promise.all(
        rawCart.map(async (entry) => {
          try {
            const url = `https://birdstool.pl/api/v1/products/${entry.slug}/`
            const res = await fetch(url, { headers: { 'Accept-Language': currentLang.value }})
            if (!res.ok) throw new Error('API error')
            const product = await res.json()

            let variant = null
            let price = 0
            const variantId = entry.variantId != null ? Number(entry.variantId) : null

            if (variantId && product.variants && product.variants.length) {
              variant = product.variants.find(v => Number(v.id) === variantId) || null
              if (variant && variant.prices && variant.prices.length) {
                const found = variant.prices.find(p => p.currency && p.currency.code === this.currencyCode)
                if (found && typeof found.amount !== 'undefined') {
                  const n = Number(found.amount)
                  if (isFinite(n)) price = n
                }
              }
            }

            if (!price) {
              const raw = product && product[priceKey] != null ? product[priceKey] : 0
              const n = Number(raw)
              price = isFinite(n) ? n : 0
            }

            return {
              productId: product.id,
              slug: product.slug,
              name: product.name,
              image: product.get_image || product.get_thumbnail,
              variant,
              variantId: variant ? variant.id : null,
              price,
              quantity: Number(entry.quantity) || 1
            }
          } catch (err) {
            // no-op (pomijamy niespójne wpisy)
            return null
          }
        })
      )

      this.cartItems = updated.filter(Boolean)

      // Lista krajów (endonimy)
      const countriesMap = new Map()
      this.cartItems.forEach(item => {
        const opts = item.variant && item.variant.shipping_options ? item.variant.shipping_options : []
        opts.forEach(opt => {
          const code = String(opt.country || '').toUpperCase()
          if (!code) return
          if (!countriesMap.has(code)) {
            const label = this.countryLabel(code, opt.country_name)
            countriesMap.set(code, label)
          }
        })
      })
      this.availableCountries = Array.from(countriesMap, ([code, name]) => ({ code, name }))

      const preferred = this.getUserCountryISO()
      const codes = this.availableCountries.map(c => c.code)
      this.selectedCountry = codes.indexOf(preferred) !== -1 ? preferred : (codes[0] || 'PL')

      this.syncHeaderBadge()
      this.summaryVisible = true;
    },

    updateLocalStorage() {
      const raw = this.cartItems.map(item => ({
        id: item.productId,
        slug: item.slug,
        variantId: item.variantId != null ? item.variantId : null,
        quantity: item.quantity
      }))
      localStorage.setItem('cart', JSON.stringify(raw))
      this.syncHeaderBadge()
    },

    syncHeaderBadge() {
      const count = this.cartItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0)
      localStorage.setItem('cart_count', String(count))
      try {
        eventBus.emit('cartUpdated', { count })
      } catch (e) {
        // no-op dla środowisk bez eventBus
        void 0
      }
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }))
    },

    increaseQty(productId, variantId) {
      const idx = this.cartItems.findIndex(i => i.productId === productId && (i.variantId || null) === (variantId || null))
      if (idx !== -1) {
        this.cartItems[idx].quantity++
        this.updateLocalStorage()
      }
    },
    decreaseQty(productId, variantId) {
      const idx = this.cartItems.findIndex(i => i.productId === productId && (i.variantId || null) === (variantId || null))
      if (idx !== -1 && this.cartItems[idx].quantity > 1) {
        this.cartItems[idx].quantity--
        this.updateLocalStorage()
      }
    },
    removeItem(productId, variantId) {
      this.cartItems = this.cartItems.filter(i => !(i.productId === productId && (i.variantId || null) === (variantId || null)))
      this.updateLocalStorage()
    }
  }
}
</script>
